import React, { Component } from 'react';

const SEVERITY_COLOR = {
  critical: '#b91c1c',
  high: '#d97706',
  medium: '#2563eb',
  low: '#6b7280',
};

const SEVERITY_LABEL = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

function getExcerpt(text, maxLen = 72) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return 'Untitled issue';
  return normalized.length <= maxLen ? normalized : `${normalized.slice(0, maxLen).trim()}…`;
}

/**
 * IssueCard — shared, fully self-contained issue card component.
 *
 * Props:
 *  issue         : object   — issue row (id, title, description, status, severity,
 *                             chapter_id, chapter_number, issue_type, suggestion)
 *  onSave?       : async ({ title, description }) => void
 *                  — omit to render title/desc as read-only text
 *  onToggleStatus: async (newStatus: 'open' | 'resolved') => void
 *  onDelete?     : async () => void
 *                  — omit to hide the delete button
 *  onApprove?    : async () => void
 *                  — shown when issue.status === 'pending'
 *  onDismiss?    : async () => void
 *                  — shown when issue.status === 'pending'
 *  defaultExpanded? : boolean (default false)
 */
export default class IssueCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isExpanded: props.defaultExpanded,
      draftTitle: props.issue.title,
      draftDescription: props.issue.description,
      isSaving: false,
      isStatusBusy: false,
      isDeleting: false,
      isApproving: false,
      isDismissing: false,
      saveError: null,
      suggestionExpanded: false,
    };
    this.saveTimer = null;
    this.lastSaved = {
      title: props.issue.title,
      description: props.issue.description,
    };
  }

  componentDidUpdate(prevProps) {
    const { issue } = this.props;
    if (prevProps.issue.title !== issue.title || prevProps.issue.description !== issue.description) {
      this.setState({ draftTitle: issue.title, draftDescription: issue.description });
      this.lastSaved = { title: issue.title, description: issue.description };
    }
  }

  componentWillUnmount() {
    this.flushPendingSaveSync();
  }

  flushPendingSaveSync = () => {
    clearTimeout(this.saveTimer);
    this.saveTimer = null;

    const { onSave } = this.props;
    if (!onSave) return;

    const trimTitle = String(this.state.draftTitle || '').trim();
    const trimDesc = String(this.state.draftDescription || '').trim();
    if (!trimTitle) return;
    if (trimTitle === this.lastSaved.title && trimDesc === this.lastSaved.description) return;

    this.setState({ isSaving: true, saveError: null });
    Promise.resolve(onSave({ title: trimTitle, description: trimDesc }))
      .then(() => {
        this.lastSaved = { title: trimTitle, description: trimDesc };
      })
      .catch(() => {
        this.setState({ saveError: 'Save failed' });
      })
      .finally(() => {
        this.setState({ isSaving: false });
      });
  };

  handleTitleChange = (e) => {
    const val = e.target.value;
    this.setState({ draftTitle: val, saveError: null });
  };

  handleDescChange = (e) => {
    const val = e.target.value;
    this.setState({ draftDescription: val, saveError: null });
  };

  handleToggleStatus = async () => {
    const { onToggleStatus, issue } = this.props;
    const newStatus = issue.status === 'done' ? 'backlog' : 'done';
    this.setState({ isStatusBusy: true });
    try {
      await onToggleStatus(newStatus);
    } finally {
      this.setState({ isStatusBusy: false });
    }
  };

  handleDelete = async () => {
    const { onDelete } = this.props;
    this.setState({ isDeleting: true });
    try {
      await onDelete();
    } finally {
      this.setState({ isDeleting: false });
    }
  };

  handleApprove = async () => {
    this.setState({ isApproving: true });
    try {
      await this.props.onApprove();
    } finally {
      this.setState({ isApproving: false });
    }
  };

  handleDismiss = async () => {
    this.setState({ isDismissing: true });
    try {
      await this.props.onDismiss();
    } finally {
      this.setState({ isDismissing: false });
    }
  };

  render() {
    const {
      issue, onSave, onDelete, onApprove, onDismiss,
      chapters, currentChapterId, onReassign,
    } = this.props;
    const {
      isExpanded, draftTitle, draftDescription,
      isSaving, isStatusBusy, isDeleting, isApproving, isDismissing,
      saveError, suggestionExpanded,
    } = this.state;

    const isPending = issue.status === 'pending';
    const isDone = issue.status === 'done';
    const isActionBusy = isStatusBusy || isSaving || isDeleting || isApproving || isDismissing;
    const color = SEVERITY_COLOR[issue.severity];
    const savedTitle = String(this.lastSaved.title || '').trim();
    const savedDescription = String(this.lastSaved.description || '').trim();
    const isTitleDirty = String(draftTitle || '').trim() !== savedTitle;
    const isDescriptionDirty = String(draftDescription || '').trim() !== savedDescription;
    const saveStatusText = isSaving
      ? (isTitleDirty && isDescriptionDirty
        ? 'Saving title and description…'
        : isTitleDirty
          ? 'Saving title…'
          : isDescriptionDirty
            ? 'Saving description…'
            : 'Saving…')
      : null;

    const isProjectLevel = !issue.chapter_id || issue.chapter_number === 0 || issue.chapter_number === null;
    const scopeLabel = isProjectLevel ? 'Project' : `Ch ${issue.chapter_number}`;
    const canReassign = Boolean(chapters && chapters.length > 1 && onReassign);
    const selectedChapterId = issue.chapter_id || currentChapterId || '';

    const displayTitle =
      isProjectLevel && /^project issue$/i.test(issue.title.trim())
        ? getExcerpt(issue.description || issue.suggestion || issue.issue_type)
        : issue.title || getExcerpt(issue.description);

    const suggestionText = issue.suggestion;
    const suggestionIsLong = suggestionText && suggestionText.length > 120;

    return (
      <div className={`issue-card${isDone ? ' issue-card--resolved' : ''}`}>
        <div
          className="issue-card-header"
          onClick={() => this.setState((s) => ({ isExpanded: !s.isExpanded }))}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && this.setState((s) => ({ isExpanded: !s.isExpanded }))}
          aria-expanded={isExpanded}
        >
          <span className="issue-card-severity-dot" style={{ background: color }} aria-hidden="true" />
          <span className="issue-card-severity-label" style={{ color }}>
            {SEVERITY_LABEL[issue.severity]}
          </span>
          {canReassign ? (
            <select
              className="issue-card-scope-select"
              value={selectedChapterId}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                const targetId = e.target.value;
                if (!targetId || targetId === selectedChapterId) return;
                onReassign(targetId);
              }}
              aria-label="Move to chapter"
              title="Move to chapter"
            >
              {!selectedChapterId && (
                <option value="">Project</option>
              )}
              {chapters.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  Ch. {ch.chapter_number ?? ch.order_no} — {ch.title}
                </option>
              ))}
            </select>
          ) : (
            <span className="issue-card-scope">{scopeLabel}</span>
          )}
          <span className="issue-card-title">{displayTitle}</span>
          {isDone && <span className="issue-card-resolved-badge">Done</span>}
          {isPending && <span className="issue-card-pending-badge">Pending</span>}
          {isSaving && <span className="issue-card-saving-badge">{saveStatusText || 'Saving…'}</span>}
          <span className="issue-card-chevron" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
        </div>

        {isExpanded && (
          <div className="issue-card-body">
            {onSave ? (
              <React.Fragment>
                <label className="issue-card-label">Title</label>
                <input
                  type="text"
                  className="issue-card-input"
                  value={draftTitle}
                  onChange={this.handleTitleChange}
                  onBlur={this.flushPendingSaveSync}
                />
                {isSaving && isTitleDirty && <span className="issue-card-field-saving">Saving title…</span>}
                <label className="issue-card-label">Content</label>
                <textarea
                  className="issue-card-textarea"
                  rows={6}
                  value={draftDescription}
                  onChange={this.handleDescChange}
                  onBlur={this.flushPendingSaveSync}
                />
                {isSaving && isDescriptionDirty && <span className="issue-card-field-saving">Saving description…</span>}
              </React.Fragment>
            ) : (
              <React.Fragment>
                {issue.title && <p className="issue-card-desc-title">{issue.title}</p>}
                {issue.description && <p className="issue-card-desc">{issue.description}</p>}
              </React.Fragment>
            )}

            {suggestionText && (
              <div
                className="issue-card-suggestion"
                onClick={suggestionIsLong ? () => this.setState((s) => ({ suggestionExpanded: !s.suggestionExpanded })) : undefined}
                style={suggestionIsLong ? { cursor: 'pointer' } : undefined}
              >
                <strong>Suggestion:</strong>{' '}
                {suggestionIsLong && !suggestionExpanded ? getExcerpt(suggestionText, 120) : suggestionText}
                {suggestionIsLong && (
                  <span className="issue-card-suggestion-chevron" aria-hidden="true">
                    {suggestionExpanded ? ' ▾' : ' ▸'}
                  </span>
                )}
              </div>
            )}
            {issue.issue_type && (
              <p className="issue-card-type">Type: {issue.issue_type.replace(/_/g, ' ')}</p>
            )}
            {saveError && <p className="issue-card-save-error">{saveError}</p>}

            <div className="issue-card-actions">
              {isPending && onApprove ? (
                <React.Fragment>
                  <button
                    type="button"
                    className="issue-card-btn issue-card-btn--resolve"
                    onClick={this.handleApprove}
                    disabled={isActionBusy}
                  >
                    {isApproving ? 'Approving…' : '✓ Approve'}
                  </button>
                  {onDismiss && (
                    <button
                      type="button"
                      className="issue-card-btn issue-card-btn--delete"
                      onClick={this.handleDismiss}
                      disabled={isActionBusy}
                    >
                      {isDismissing ? 'Dismissing…' : '✗ Dismiss'}
                    </button>
                  )}
                </React.Fragment>
              ) : (
                <button
                  type="button"
                  className={`issue-card-btn${isDone ? ' issue-card-btn--reopen' : ' issue-card-btn--resolve'}`}
                  onClick={this.handleToggleStatus}
                  disabled={isActionBusy}
                >
                  {isStatusBusy ? 'Saving…' : isDone ? 'Reopen' : 'Mark Done'}
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="issue-card-btn issue-card-btn--delete"
                  onClick={this.handleDelete}
                  disabled={isActionBusy}
                >
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
